import { useEffect, useState } from 'react'
import CompactKlimtWrapper from '@eversports/klimt-design-components/CompactKlimtWrapper'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Link from '@eversports/klimt-primitives/legacy/Link'
import Button from '@eversports/klimt-primitives/legacy/Button'
import Text from '@eversports/klimt-primitives/legacy/Text'
import Icon from '@eversports/klimt-primitives/legacy/Icon'
import { useLocation } from 'react-router-dom'
import ShareIcon from '../../assets/icons/share.svg'

import useConfirmationActions from '../../data/useConfirmationActions'
import useCheckoutContext from '../../data/useCheckoutContext'

import { Localized } from '../../localization/react'
import Venue from '../../typings/Venue'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { Origin } from '../../graphql'
import SpotSelectionIcon from './img/spotSelection.svg'
import InviteFriendsDialog from './InviteFriendsDialog'
import { SPOT_BOOKING_FROM } from './Confirmation.constants'

interface Props {
  venue: Venue
  eventId?: string
  hasBooking: boolean
  invoiceDownloadLink?: string | null
  voucherCode?: string
}

const InviteFriendsModalLocalStorageKey = 'hasSeenEventInviteFriendsModal'

const ConfirmationActions = ({ venue, eventId, hasBooking, invoiceDownloadLink, voucherCode }: Props) => {
  const [showInviteFriendsDialog, setShowInviteFriendsDialog] = useState(false)
  const { goToMyBookings, goBackToWebsite, goToMyRecording, goToModifySpots, hasSpotBooking } = useConfirmationActions()
  const { isVenueBrandingEnabled } = useCheckoutContext()
  const voucherDownloadLink = voucherCode ? `/widget/${venue.shortId}/voucher/code/${voucherCode}/html` : undefined

  const location = useLocation()
  const { origin, hideEversportsBranding } = getPhoenixUrlParams(location.search)
  const isWidget = [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)

  useEffect(() => {
    let timer: null | ReturnType<typeof setTimeout> = null
    const hasSeenInviteFriendsModal = window.localStorage.getItem(InviteFriendsModalLocalStorageKey)

    if (!hasSeenInviteFriendsModal) {
      timer = setTimeout(() => setShowInviteFriendsDialog(true), 1000)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  const toggleDialog = () => {
    setShowInviteFriendsDialog(!showInviteFriendsDialog)
    if (!window.localStorage.getItem(InviteFriendsModalLocalStorageKey)) {
      window.localStorage.setItem(InviteFriendsModalLocalStorageKey, 'true')
    }
  }

  if (isVenueBrandingEnabled || hideEversportsBranding) {
    return (
      <CompactKlimtWrapper>
        <Stack gap={4} width="100%" sx={{ '& > *': { flex: 1, width: '100%' } }}>
          {goToModifySpots && hasSpotBooking && (
            <Link to={goToModifySpots} external>
              <Button variant="primary" sx={{ width: '100%', padding: 2 }}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <Icon src={SpotSelectionIcon} size="small" color="white" />
                  <Text sx={{ color: 'white' }}>
                    <Localized id="confirmation-button-modify-spot" />
                  </Text>
                </Stack>
              </Button>
            </Link>
          )}
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="center"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={4}
          width="100%"
          sx={{ '& > *': { flex: 1, width: '100%' } }}
        >
          {voucherDownloadLink && (
            <Link to={voucherDownloadLink} external blank>
              <Button variant="primary" sx={{ width: '100%', padding: 2 }}>
                <Text sx={{ color: 'white' }}>
                  <Localized id="download-voucher" />
                </Text>
              </Button>
            </Link>
          )}
          {hasBooking && (
            <Link to={goToMyBookings} external>
              <Button variant={goToModifySpots ? 'secondary' : 'primary'} sx={{ width: '100%', padding: 2 }}>
                <Text sx={{ color: goToModifySpots ? 'primary.main' : 'white' }}>
                  <Localized id="confirmation-button-my-bookings" />
                </Text>
              </Button>
            </Link>
          )}
          {goToMyRecording && (
            <Link to={goToMyRecording} external>
              <Button variant="secondary" sx={{ width: '100%', padding: 2 }}>
                <Text sx={{ color: 'primary.main' }}>
                  <Localized id="confirmation-button-my-recording" />
                </Text>
              </Button>
            </Link>
          )}
          {!hasBooking && invoiceDownloadLink && (
            <Button
              variant={voucherDownloadLink ? 'secondary' : 'primary'}
              sx={{ width: '100%', padding: 2 }}
              onClick={() => window.open(invoiceDownloadLink, '_blank')}
            >
              <Text sx={{ color: voucherDownloadLink ? 'primary.main' : 'white' }}>
                <Localized id="download-invoice" />
              </Text>
            </Button>
          )}
        </Stack>
      </CompactKlimtWrapper>
    )
  }

  return (
    <CompactKlimtWrapper>
      {goToModifySpots && hasSpotBooking && (
        <Stack gap={4} width="100%" sx={{ '& > *': { flex: 1, width: '100%' } }}>
          <Link
            to={isWidget ? goToModifySpots : `${goToModifySpots}?from=${SPOT_BOOKING_FROM.MODIFY_YOUR_SPOT_BUTTON}`}
            external
          >
            <Button variant="primary" sx={{ width: '100%', padding: 2 }}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Icon src={SpotSelectionIcon} size="small" color="white" />
                <Text sx={{ color: 'white' }}>
                  <Localized id="confirmation-button-modify-spot" />
                </Text>
              </Stack>
            </Button>
          </Link>
        </Stack>
      )}
      <Stack
        alignItems="center"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={4}
        width="100%"
        sx={{ '& > *': { flex: 1, width: '100%' } }}
      >
        {voucherDownloadLink && (
          <Link to={voucherDownloadLink} external blank>
            <Button variant="primary" sx={{ width: '100%', padding: 2 }}>
              <Text sx={{ color: 'white' }}>
                <Localized id="download-voucher" />
              </Text>
            </Button>
          </Link>
        )}
        {hasBooking && eventId && (
          <>
            <Button variant={goToModifySpots ? 'secondary' : 'primary'} sx={{ padding: 2 }} onClick={toggleDialog}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Icon size="small" color="white" src={ShareIcon} />
                <Text sx={{ color: goToModifySpots ? 'primary.main' : 'white' }}>
                  <Localized id="confirmation-button-invite-friends" />
                </Text>
              </Stack>
            </Button>
            <InviteFriendsDialog isOpen={showInviteFriendsDialog} toggleDialog={toggleDialog} eventId={eventId} />
          </>
        )}
        <Link to={goBackToWebsite} external>
          <Button variant={hasBooking ? 'secondary' : 'primary'} sx={{ width: '100%', padding: 2 }}>
            <Text sx={{ color: hasBooking ? 'primary.main' : 'white' }}>
              <Localized id="confirmation-button-website" />
            </Text>
          </Button>
        </Link>
        {hasBooking && (
          <Link to={goToMyBookings} external>
            <Button variant="secondary" sx={{ width: '100%', padding: 2 }}>
              <Text sx={{ color: 'primary.main' }}>
                <Localized id="confirmation-button-my-bookings" />
              </Text>
            </Button>
          </Link>
        )}
        {goToMyRecording && (
          <Link to={goToMyRecording} external>
            <Button variant="secondary" sx={{ width: '100%', padding: 2 }}>
              <Text sx={{ color: 'primary.main' }}>
                <Localized id="confirmation-button-my-recording" />
              </Text>
            </Button>
          </Link>
        )}
        {!hasBooking && invoiceDownloadLink && (
          <Button variant="secondary" sx={{ padding: 2 }} onClick={() => window.open(invoiceDownloadLink, '_blank')}>
            <Text sx={{ color: 'primary.main' }}>
              <Localized id="download-invoice" />
            </Text>
          </Button>
        )}
      </Stack>
    </CompactKlimtWrapper>
  )
}

export default ConfirmationActions
